import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MockInterviewsSubTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function MockInterviewsSubTab() {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingInterview, setEditingInterview] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [formData, setFormData] = useState({
    company_id: '',
    title: '',
    position: '',
    grade: 'Junior',
    salary_range: ''
  });
  const [copyLinkFeedback, setCopyLinkFeedback] = useState(null);

  useEffect(() => {
    fetchInterviews();
    fetchCompanies();
  }, []);

  const fetchInterviews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/mock-interviews`);
      setInterviews(response.data);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке mock-собеседований: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/companies`);
      setCompanies(response.data);
    } catch (err) {
      console.error('Ошибка при загрузке компаний:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        company_id: parseInt(formData.company_id)
      };
      if (editingInterview) {
        await axios.put(`${API_URL}/api/mock-interviews/${editingInterview.id}`, submitData);
      } else {
        await axios.post(`${API_URL}/api/mock-interviews`, {
          ...submitData,
          questions: []
        });
      }
      setShowForm(false);
      setEditingInterview(null);
      setFormData({
        company_id: '',
        title: '',
        position: '',
        grade: 'Junior',
        salary_range: ''
      });
      fetchInterviews();
    } catch (err) {
      setError('Ошибка при сохранении mock-собеседования: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleEdit = (interview) => {
    setEditingInterview(interview);
    setFormData({
      company_id: interview.company_id.toString(),
      title: interview.title,
      position: interview.position,
      grade: interview.grade,
      salary_range: interview.salary_range
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить это mock-собеседование? Все связанные вопросы также будут удалены.')) {
      try {
        await axios.delete(`${API_URL}/api/mock-interviews/${id}`);
        fetchInterviews();
      } catch (err) {
        setError('Ошибка при удалении mock-собеседования: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleCopyShareLink = async (interview) => {
    setCopyLinkFeedback(null);
    try {
      const response = await axios.get(`${API_URL}/api/mock-interviews/${interview.id}/share-link`);
      const url = response.data?.url || `https://t.me/itinterview_tgbot?start=interview_${response.data?.share_id || ''}`;
      await navigator.clipboard.writeText(url);
      setCopyLinkFeedback(interview.id);
      setTimeout(() => setCopyLinkFeedback(null), 2000);
    } catch (err) {
      setError('Ошибка при получении ссылки: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingInterview(null);
    setFormData({
      company_id: '',
      title: '',
      position: '',
      grade: 'Junior',
      salary_range: ''
    });
  };

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="mock-interviews-sub-tab">
      <div className="tab-header">
        <h3>Управление Mock-собеседованиями</h3>
        {!showForm && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + Добавить mock-собеседование
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <div className="form-container">
          <h4>{editingInterview ? 'Редактировать mock-собеседование' : 'Добавить новое mock-собеседование'}</h4>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="company_id">Компания *</label>
              <select
                id="company_id"
                value={formData.company_id}
                onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                required
              >
                <option value="">Выберите компанию</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="title">Название mock-собеседования *</label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                maxLength={255}
                placeholder="Введите название"
              />
            </div>
            <div className="form-group">
              <label htmlFor="position">Название должности *</label>
              <input
                type="text"
                id="position"
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                required
                maxLength={255}
                placeholder="Например: Backend Developer"
              />
            </div>
            <div className="form-group">
              <label htmlFor="grade">Грейд *</label>
              <select
                id="grade"
                value={formData.grade}
                onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                required
              >
                <option value="Junior">Junior</option>
                <option value="Middle">Middle</option>
                <option value="Senior">Senior</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="salary_range">Зарплатная вилка *</label>
              <input
                type="text"
                id="salary_range"
                value={formData.salary_range}
                onChange={(e) => setFormData({ ...formData, salary_range: e.target.value })}
                required
                maxLength={255}
                placeholder="Например: 150000-250000 руб."
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingInterview ? 'Сохранить' : 'Создать'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Название</th>
              <th>Компания</th>
              <th>Должность</th>
              <th>Грейд</th>
              <th>Зарплатная вилка</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {interviews.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-state">
                  Нет mock-собеседований. Добавьте первое mock-собеседование.
                </td>
              </tr>
            ) : (
              interviews.map((interview) => (
                <tr key={interview.id}>
                  <td>{interview.id}</td>
                  <td>{interview.title}</td>
                  <td>{interview.company_name || '-'}</td>
                  <td>{interview.position}</td>
                  <td>{interview.grade}</td>
                  <td>{interview.salary_range}</td>
                  <td>
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(interview)}
                      title="Редактировать"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon btn-copy-link"
                      onClick={() => handleCopyShareLink(interview)}
                      title="Скопировать ссылку"
                    >
                      {copyLinkFeedback === interview.id ? '✓' : '🔗'}
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(interview.id)}
                      title="Удалить"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MockInterviewsSubTab;

